/**
 * Модуль для работы с превью боя
 * Вынесено из legacy-app.js для модульности
 */

/**
 * Форматирует данные существа для превью боя
 * @param {object} creature - Объект существа
 * @param {function} normalizeStats - Функция нормализации статистики
 * @param {function} formatRarityName - Функция форматирования редкости (St)
 * @param {object} currentUser - Текущий пользователь
 * @returns {object|null} Объект превью или null
 */
export function formatCreaturePreview(creature, normalizeStats, formatRarityName, currentUser) {
  if (!creature) return null;
  
  const baseAttributes = creature.base_attributes || creature.baseAttributes || {};
  const baseStatsRaw =
    baseAttributes.baseStats ||
    baseAttributes.stats ||
    baseAttributes.base_stats ||
    creature.baseStats ||
    creature.stats ||
    null;
  const baseStats = normalizeStats(baseStatsRaw);
  
  // Рассчитываем статистику с учетом уровня
  const level = creature.level || 1;
  const professionLevel = creature.profession_level || 0;
  
  // Базовые значения по редкости
  const rarity = (() => {
    const r = baseAttributes.rarity || creature.rarity;
    if (typeof r === 'string') return r.toLowerCase();
    if (r && typeof r === 'object' && r.grade) return String(r.grade).toLowerCase();
    return null;
  })();
  
  const RARITY_BASE_STATS = {
    common: { hp: 100, attack: 20, defense: 15, speed: 10, critChance: 0.05, energy: 100 },
    rare: { hp: 112, attack: 23, defense: 18, speed: 11.5, critChance: 0.06, energy: 105 },
    epic: { hp: 126, attack: 27, defense: 22, speed: 13, critChance: 0.07, energy: 112 },
    legendary: { hp: 140, attack: 32, defense: 26, speed: 15.5, critChance: 0.09, energy: 120 }
  };
  
  const DEFAULT_STATS = { hp: 100, attack: 20, defense: 15, speed: 10, critChance: 0.05, energy: 100 };
  const baseline = (rarity && RARITY_BASE_STATS[rarity]) ? RARITY_BASE_STATS[rarity] : DEFAULT_STATS;
  
  // Используем базовую статистику из данных или значения по умолчанию
  const stats = {
    hp: baseStats.hp || baseline.hp,
    attack: baseStats.attack || baseline.attack,
    defense: baseStats.defense || baseline.defense,
    speed: baseStats.speed || baseline.speed,
    critChance: baseStats.critChance || baseline.critChance,
    energy: baseStats.energy || baseline.energy,
  };
  
  // Применяем модификаторы уровня
  const levelMultiplier = 1 + (level - 1) * 0.05;
  const professionMultiplier = 1 + professionLevel * 0.02;
  
  const finalStats = {
    hp: Math.round(stats.hp * levelMultiplier * professionMultiplier),
    attack: stats.attack * levelMultiplier * professionMultiplier,
    defense: stats.defense * levelMultiplier * professionMultiplier,
    speed: stats.speed * levelMultiplier * professionMultiplier,
    critChance: Math.min(stats.critChance * (1 + (level - 1) * 0.01), 0.5),
    energy: Math.round(stats.energy * levelMultiplier),
  };
  
  return {
    name: creature.name || creature.base_name || "Неизвестное существо",
    type: creature.type || creature.base_type || "unknown",
    rarity: rarity || "common",
    level: level,
    professionLevel: professionLevel,
    stats: finalStats,
    image: creature.image || creature.base_image || null,
    description: creature.description || creature.base_description || null,
    owner: currentUser?.username || currentUser?.first_name || null,
  };
}

/**
 * Форматирует данные AI пресета для превью боя
 * @param {object} preset - Объект AI пресета
 * @param {function} normalizeStats - Функция нормализации статистики
 * @param {function} formatRarityName - Функция форматирования редкости
 * @returns {object|null} Объект превью или null
 */
export function formatAiPresetPreview(preset, normalizeStats, formatRarityName) {
  if (!preset) return null;
  
  const baseStats = normalizeStats(preset.baseStats || preset.stats || {});
  
  return {
    name: preset.name || "AI Противник",
    type: preset.type || "unknown",
    rarity: preset.rarity || "common",
    level: preset.level || 1,
    professionLevel: 0,
    stats: {
      hp: baseStats.hp || 100,
      attack: baseStats.attack || 20,
      defense: baseStats.defense || 15,
      speed: baseStats.speed || 10,
      critChance: baseStats.critChance || 0.05,
      energy: baseStats.energy || 100,
    },
    image: preset.image || null,
    description: preset.description || null,
    owner: null,
  };
}

/**
 * Форматирует данные случайного существа для превью боя
 * @param {boolean} isEnemy - Является ли противником
 * @param {number} collectionSize - Размер коллекции
 * @param {object} defaultStats - Статистика по умолчанию
 * @param {object} currentUser - Текущий пользователь
 * @returns {object|null} Объект превью или null
 */
export function formatRandomCreaturePreview(isEnemy, collectionSize, defaultStats, currentUser) {
  return {
    name: isEnemy ? "Случайный противник" : "Случайное существо",
    type: "random",
    rarity: "common",
    level: 1,
    professionLevel: 0,
    stats: defaultStats || {
      hp: 100,
      attack: 20,
      defense: 15,
      speed: 10,
      critChance: 0.05,
      energy: 100,
    },
    image: null,
    description: isEnemy 
      ? "Случайный противник будет выбран автоматически"
      : "Случайное существо из вашей коллекции",
    owner: isEnemy ? null : (currentUser?.username || currentUser?.first_name || null),
  };
}

/**
 * Получает превью существа на основе селектора
 * @param {string} selector - Селектор существа (user:ID, user:random, или AI preset ID)
 * @param {string} side - Сторона ("player" или "enemy")
 * @param {function} parseUserCreatureId - Функция парсинга ID пользовательского существа
 * @param {function} findCreatureInCollection - Функция поиска существа в коллекции
 * @param {function} findAiPreset - Функция поиска AI пресета
 * @param {function} formatCreaturePreview - Функция форматирования превью существа
 * @param {function} formatAiPresetPreview - Функция форматирования превью AI пресета
 * @param {function} formatRandomCreaturePreview - Функция форматирования случайного существа
 * @param {object} options - Дополнительные опции
 * @param {array} options.collection - Коллекция существ
 * @param {object} options.currentUser - Текущий пользователь
 * @param {object} options.defaultStats - Статистика по умолчанию
 * @param {function} options.normalizeStats - Функция нормализации статистики
 * @param {function} options.formatRarityName - Функция форматирования редкости
 * @returns {object|null} Объект превью или null
 */
export function getCreaturePreview(
  selector,
  side,
  parseUserCreatureId,
  findCreatureInCollection,
  findAiPreset,
  formatCreaturePreview,
  formatAiPresetPreview,
  formatRandomCreaturePreview,
  options
) {
  if (!selector) return null;
  
  if (selector === "user:random") {
    return formatRandomCreaturePreview(
      side === "enemy",
      options.collection?.length || 0,
      options.defaultStats,
      options.currentUser
    );
  }
  
  if (selector.startsWith("user:")) {
    const { creatureId, userCreatureId } = parseUserCreatureId(selector);
    const creature = findCreatureInCollection(creatureId, userCreatureId);
    return formatCreaturePreview(
      creature,
      options.normalizeStats,
      options.formatRarityName,
      options.currentUser
    );
  }
  
  const preset = findAiPreset(selector);
  return preset 
    ? formatAiPresetPreview(preset, options.normalizeStats, options.formatRarityName)
    : null;
}

/**
 * Форматирует статистику для HTML превью
 * @param {object} stats - Объект статистики
 * @param {number} critChance - Шанс критического удара
 * @param {object} statLimits - Лимиты статистики
 * @returns {string} HTML строка
 */
export function formatPreviewStatsHtml(stats, critChance, statLimits) {
  if (!stats || !statLimits) return "";
  
  const statRows = [
    { key: "hp", label: "Здоровье:", cap: statLimits.hp },
    { key: "attack", label: "Атака:", cap: statLimits.attack },
    { key: "defense", label: "Защита:", cap: statLimits.defense },
    { key: "speed", label: "Скорость:", cap: statLimits.speed },
    { key: "energy", label: "Энергия:", cap: statLimits.energy },
  ];
  
  const htmlParts = [];
  statRows.forEach((statRow) => {
    const value = stats[statRow.key];
    if (value == null) return;
    const formattedValue = statRow.key === "attack" || statRow.key === "defense" || statRow.key === "speed"
      ? value.toFixed(1)
      : Math.round(value);
    htmlParts.push(`
      <div class="battle-stat-row">
        <span>${statRow.label}</span>
        <span style="font-weight:600;">${formattedValue}</span>
      </div>
    `);
  });
  
  if (critChance != null && !Number.isNaN(critChance)) {
    htmlParts.push(`
      <div class="battle-stat-row" style="justify-content: space-between;">
        <span>Крит. шанс:</span>
        <span style="font-weight:600;">${(critChance * 100).toFixed(1)}%</span>
      </div>
    `);
  }
  
  return htmlParts.length > 0 ? `<div class="battle-stats">${htmlParts.join("")}</div>` : "";
}

/**
 * Рендерит превью существа в DOM
 * @param {string} elementId - ID элемента для рендеринга
 * @param {object|null} preview - Объект превью или null
 * @param {string} side - Сторона ("player" или "enemy")
 * @param {function} escapeHtml - Функция экранирования HTML
 * @param {object} typeNames - Объект с именами типов
 * @param {function} formatPreviewStatsHtml - Функция форматирования статистики
 * @param {object} statLimits - Лимиты статистики
 */
export function renderCreaturePreview(elementId, preview, side, escapeHtml, typeNames, formatPreviewStatsHtml, statLimits) {
  const element = document.getElementById(elementId);
  if (!element) return;
  
  if (!preview) {
    element.innerHTML = `
      <div class="battle-empty-state">
        Выберите ${side === "enemy" ? "противника" : "своего бойца"}, чтобы увидеть превью.
      </div>
    `;
    return;
  }
  
  const typeName = typeNames[preview.type] || preview.type;
  const rarityName = formatRarityName ? formatRarityName(preview.rarity) : preview.rarity;
  
  element.innerHTML = `
    <div class="battle-preview-card">
      <div class="battle-preview-header">
        ${preview.image 
          ? `<img src="${escapeHtml(preview.image)}" alt="${escapeHtml(preview.name)}" class="battle-preview-image" />`
          : `<div class="battle-preview-placeholder">${escapeHtml(preview.name)}</div>`
        }
      </div>
      <div class="battle-preview-body">
        <h3 class="battle-preview-name">${escapeHtml(preview.name)}</h3>
        <div class="battle-preview-meta">
          <span class="battle-preview-type">${escapeHtml(typeName)}</span>
          <span class="battle-preview-rarity">${escapeHtml(rarityName)}</span>
          ${preview.level ? `<span class="battle-preview-level">Ур. ${preview.level}</span>` : ""}
        </div>
        ${formatPreviewStatsHtml(preview.stats, preview.stats.critChance, statLimits)}
        ${preview.description 
          ? `<div class="battle-preview-description">${escapeHtml(preview.description)}</div>`
          : ""
        }
        ${preview.owner 
          ? `<div class="battle-preview-owner">Владелец: ${escapeHtml(preview.owner)}</div>`
          : ""
        }
      </div>
    </div>
  `;
}
